<app-admin-student-platform-header></app-admin-student-platform-header>

<div class="dashboard-page-container">
  <!-- Top Bar: Title & Controls -->
  <div class="dashboard-header-bar">
    <div class="header-titles">
      <h1 class="main-title">{{ getHeaderTitle() }}</h1>
      <p class="subtitle">
        Everything on one screen. Filter by college or company, and view real-time counts across learning and hiring.
      </p>
    </div>

    <div class="header-controls">
      <!-- Scope / Entity Selector Dropdown -->
      <div class="entity-selector-container" #entityDropdownRef>
        <button 
          class="entity-selector-btn" 
          type="button"
          (click)="toggleEntityDropdown()"
          [class.active]="isDropdownOpen"
          [title]="getScopeButtonTooltip()"
        >
          <span class="material-icons scope-icon">{{ getScopeIcon() }}</span>
          <span class="scope-label">{{ getScopeButtonText() }}</span>
          <span class="material-icons arrow-icon">arrow_drop_down</span>
        </button>

        <!-- Dropdown Menu Overlay -->
        <div class="entity-dropdown-menu" *ngIf="isDropdownOpen">
          <!-- Filter Category Tabs -->
          <div class="entity-type-tabs" (click)="$event.stopPropagation()">
            <button 
              type="button" 
              class="tab-btn" 
              [class.active]="activeEntityTab === 'platform'" 
              (click)="setEntityTab('platform')"
            >Entire platform</button>
            <button 
              type="button" 
              class="tab-btn" 
              [class.active]="activeEntityTab === 'colleges'" 
              (click)="setEntityTab('colleges')"
            >Colleges</button>
            <button 
              type="button" 
              class="tab-btn" 
              [class.active]="activeEntityTab === 'companies'" 
              (click)="setEntityTab('companies')"
            >Companies</button>
          </div>

          <!-- Search input (Only shown when searching Colleges or Companies) -->
          <div class="search-box-wrapper" *ngIf="activeEntityTab !== 'platform'" (click)="$event.stopPropagation()">
            <span class="material-icons search-icon">search</span>
            <input 
              type="text" 
              class="search-input" 
              [placeholder]="getSearchPlaceholder()" 
              [(ngModel)]="searchQuery"
              (ngModelChange)="onSearchChange($event)"
            />
            <button 
              *ngIf="searchQuery" 
              class="clear-search-btn" 
              (click)="clearSearch($event)"
            >
              <span class="material-icons">close</span>
            </button>
          </div>

          <!-- Dropdown List Options -->
          <div class="entity-list-scroll">
            <!-- Entire Platform Option (shown when tab is platform) -->
            <div 
              *ngIf="activeEntityTab === 'platform'" 
              class="entity-item platform-item" 
              [class.selected]="currentScope === 'platform'"
              (click)="selectScope('platform')"
            >
              <div class="item-icon-box bg-purple">
                <span class="material-icons">public</span>
              </div>
              <div class="item-info">
                <span class="item-name">Entire platform</span>
                <span class="item-desc">All colleges and companies</span>
              </div>
              <span *ngIf="currentScope === 'platform'" class="material-icons check-icon">check</span>
            </div>

            <!-- Loading Spinner in dropdown -->
            <div *ngIf="isSearchingEntities" class="dropdown-loading">
              <span class="spinner"></span>
              <span>Searching...</span>
            </div>

            <!-- Colleges Section (shown when tab is colleges) -->
            <div *ngIf="!isSearchingEntities && activeEntityTab === 'colleges'" class="section-group">
              <div class="group-header">
                <span>COLLEGES ({{ collegesList.length }})</span>
              </div>
              <div 
                *ngFor="let col of collegesList" 
                class="entity-item" 
                [class.selected]="currentScope === 'college' && selectedEntityId === col.id"
                (click)="selectCollege(col)"
              >
                <div class="item-icon-box bg-green">
                  <span class="material-icons">school</span>
                </div>
                <div class="item-info">
                  <span class="item-name">{{ col.name }}</span>
                  <span class="item-desc" *ngIf="col.city || col.state">
                    {{ col.city }}{{ col.city && col.state ? ', ' : '' }}{{ col.state }}
                  </span>
                </div>
                <span *ngIf="currentScope === 'college' && selectedEntityId === col.id" class="material-icons check-icon">check</span>
              </div>
              <div *ngIf="collegesList.length === 0" class="empty-group-msg">No colleges found</div>
            </div>

            <!-- Companies Section (shown when tab is companies) -->
            <div *ngIf="!isSearchingEntities && activeEntityTab === 'companies'" class="section-group">
              <div class="group-header">
                <span>COMPANIES ({{ companiesList.length }})</span>
              </div>
              <div 
                *ngFor="let comp of companiesList" 
                class="entity-item" 
                [class.selected]="currentScope === 'company' && selectedEntityId === comp.id"
                (click)="selectCompany(comp)"
              >
                <div class="item-icon-box bg-sky">
                  <span class="material-icons">business</span>
                </div>
                <div class="item-info">
                  <span class="item-name">{{ comp.name }}</span>
                  <span class="item-desc" *ngIf="comp.city">
                    {{ comp.city }}
                  </span>
                </div>
                <span *ngIf="currentScope === 'company' && selectedEntityId === comp.id" class="material-icons check-icon">check</span>
              </div>
              <div *ngIf="companiesList.length === 0" class="empty-group-msg">No companies found</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Time Range Pills -->
      <div class="time-range-pills">
        <button 
          *ngFor="let pill of timeRangeOptions" 
          type="button" 
          class="pill-btn" 
          [class.active]="selectedTimeRange === pill.value"
          (click)="onTimeRangeSelect(pill.value)"
        >
          {{ pill.label }}
        </button>
      </div>
    </div>
  </div>

  <!-- Selected Entity Detail Banner (Page 3 & Page 4 in PDF) -->
  <div *ngIf="currentScope !== 'platform' && dashboardData?.entity_info" class="entity-detail-banner">
    <div class="banner-left">
      <div class="banner-avatar" [ngClass]="currentScope === 'college' ? 'bg-green' : 'bg-sky'">
        <span class="material-icons">{{ currentScope === 'college' ? 'school' : 'business' }}</span>
      </div>
      <div class="banner-info">
        <div class="banner-header-row">
          <h2 class="entity-title">{{ dashboardData?.entity_info?.name }}</h2>
          <span class="entity-type-badge">
            {{ currentScope === 'college' ? 'College' : 'Company' }}
            <span *ngIf="dashboardData?.entity_info?.city">• {{ dashboardData?.entity_info?.city }}</span>
            <span *ngIf="dashboardData?.entity_info?.state">, {{ dashboardData?.entity_info?.state }}</span>
          </span>
        </div>
        <p class="banner-subtext">
          {{ currentScope === 'college' 
              ? "Showing this college's students, their practice activity, and placements." 
              : "Showing this company's jobs, screening interviews, and hires." 
          }}
        </p>
      </div>
    </div>
    <div class="banner-right">
      <button class="clear-filter-btn" type="button" (click)="resetToPlatform()">
        <span class="material-icons">close</span>
        Clear filter
      </button>
    </div>
  </div>

  <!-- Loading Skeleton State -->
  <div class="skeleton-grid-container" *ngIf="isLoading">
    <div class="skeleton-card" *ngFor="let i of [1,2,3,4]">
      <div class="skeleton-header">
        <div class="skeleton-line sm"></div>
        <div class="skeleton-icon-box"></div>
      </div>
      <div class="skeleton-line lg"></div>
    </div>
  </div>

  <!-- Error State -->
  <div class="error-banner" *ngIf="!isLoading && hasError">
    <span class="material-icons">error_outline</span>
    <span>Failed to load dashboard overview. Please check connection and try again.</span>
    <button class="retry-btn" (click)="loadDashboardData()">Retry</button>
  </div>

  <!-- Main Dashboard Content -->
  <div *ngIf="!isLoading && dashboardData" class="dashboard-content-sections">

    <!-- Section 1: Overview Cards (4 cards) -->
    <div class="dashboard-section">
      <div class="cards-grid overview-grid">
        <div class="metric-card" *ngFor="let card of dashboardData.overview_cards">
          <div class="card-header">
            <span class="card-label">{{ card.label }}</span>
            <div class="card-icon" [ngClass]="getCardIconStyle(card.key).iconBg">
              <span class="material-icons">{{ getCardIconStyle(card.key).icon }}</span>
            </div>
          </div>
          <div class="card-body">
            <span class="card-value">{{ card.value | number }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Learning & Practice (Platform & College only) -->
    <div class="dashboard-section" *ngIf="currentScope !== 'company' && dashboardData.learning_and_practice.length > 0">
      <div class="section-title-bar">
        <h2 class="section-title">Learning & practice</h2>
      </div>

      <div class="cards-grid sub-section-grid">
        <div class="metric-card" *ngFor="let card of dashboardData.learning_and_practice">
          <div class="card-header">
            <span class="card-label">{{ card.label }}</span>
            <div class="card-icon" [ngClass]="getCardIconStyle(card.key).iconBg">
              <span class="material-icons">{{ getCardIconStyle(card.key).icon }}</span>
            </div>
          </div>
          <div class="card-body">
            <span class="card-value">{{ card.value | number }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 3: Hiring & Placement -->
    <div class="dashboard-section" *ngIf="dashboardData.hiring_and_placement.length > 0">
      <div class="section-title-bar">
        <h2 class="section-title">Hiring & placement</h2>
      </div>

      <div class="cards-grid sub-section-grid">
        <div class="metric-card" *ngFor="let card of dashboardData.hiring_and_placement">
          <div class="card-header">
            <span class="card-label">{{ card.label }}</span>
            <div class="card-icon" [ngClass]="getCardIconStyle(card.key).iconBg">
              <span class="material-icons">{{ getCardIconStyle(card.key).icon }}</span>
            </div>
          </div>
          <div class="card-body">
            <span class="card-value">{{ card.value | number }}</span>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>